import { Injectable } from '@angular/core';
import { FinancePlan } from 'app/shared/models';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { FuseUtils } from '@fuse/utils';

@Injectable()
export class FinancePlanService {
  filterBy: string;
  onFilterChanged: Subject<any>;
  private _plans: FinancePlan[];
  onFinancePlanChanged: BehaviorSubject<any>;
  onSearchTextChanged: Subject<any>;

  constructor(private _http: HttpClient) {
    // Set the defaults
    this.onFilterChanged = new Subject();
    this._plans = [];
    this.onFinancePlanChanged = new BehaviorSubject(this._plans);
    this.onSearchTextChanged = new Subject();
  }

  get plans(): FinancePlan[] {
    return this._plans;
  }

  // Resolver
  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<any> | Promise<any> | any {
    return new Promise((resolve, reject) => {
      Promise.all([this.getAllFinancePlans({ all: true })]).then(() => {
        this.onFilterChanged.subscribe((filter) => {
          this.filterBy = filter;
          if (this.filterBy === 'all' || !this.filterBy) {
            this.onFinancePlanChanged.next(this._plans);
            return;
          }
          const filtered = this._plans.filter(
            (plan) => plan.providerCode && plan.providerCode.toLowerCase() === this.filterBy.toLowerCase()
          );
          this.onFinancePlanChanged.next(filtered);
        });

        this.onSearchTextChanged.subscribe((searchText) => {
          const searched = FuseUtils.filterArrayByString(this._plans, searchText);
          this.onFinancePlanChanged.next(searched);
        });

        resolve(this._plans);
      }, reject);
    });
  }

  // Get All Finance Plans
  getAllFinancePlans(params?: { all?: boolean; active?: boolean | string }): Promise<FinancePlan[]> {
    return new Promise((resolve, reject) => {
      let query = '';
      if (params) {
        const q: string[] = [];
        if (params.all !== undefined) q.push(`all=${params.all}`);
        if (params.active !== undefined) q.push(`active=${params.active}`);
        if (q.length > 0) query = `?${q.join('&')}`;
      }
      this._http.get(`/api/salespro/financeplan${query}`).subscribe((response: any) => {
        this._plans = Array.isArray(response) ? response.map((item) => new FinancePlan(item)) : [];
        this.onFinancePlanChanged.next(this._plans);
        resolve(this._plans);
      }, reject);
    });
  }

  // Add New Finance Plan
  addNewFinancePlan(newPlan: FinancePlan): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http
        .post(`/api/salespro/financeplan`, newPlan)
        .subscribe((response: any) => {
          const resultPlan = new FinancePlan(response);
          this._plans.push(resultPlan);
          this.onFinancePlanChanged.next(this._plans);
          resolve(resultPlan);
        }, reject);
    });
  }

  // Update Selected Finance Plan
  updateFinancePlan(oldPlan: FinancePlan, newPlanData: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http
        .patch(`/api/salespro/financeplan/${oldPlan.id}`, newPlanData)
        .subscribe((response: any) => {
          const index = this._plans.findIndex((p) => p.id === oldPlan.id);
          if (index !== -1) {
            oldPlan.update(newPlanData);
            this._plans[index] = oldPlan;
            this.onFinancePlanChanged.next(this._plans);
          }
          resolve(response);
        }, reject);
    });
  }

  // Delete Selected Finance Plan
  deleteFinancePlan(selectedPlan: FinancePlan): Promise<any> {
    return new Promise((resolve, reject) => {
      this._http
        .delete(`/api/salespro/financeplan/${selectedPlan.id}`)
        .subscribe((response: any) => {
          const index = this._plans.findIndex((p) => p.id === selectedPlan.id);
          if (index !== -1) {
            this._plans.splice(index, 1);
            this.onFinancePlanChanged.next(this._plans);
          }
          resolve(response);
        }, reject);
    });
  }
}
